import { useState, useEffect, useRef } from "react";
import { Gift, ChevronRight, Loader2, Check, ExternalLink, Star, ShoppingBag } from "lucide-react";
import { getGiftDataByRecipientEmail, getWishItems, type Wishlist, type WishItem } from "@/services/wishlists";
import { updateWishItem } from "@/services/wishlists";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";
import BlurImage from "./BlurImage";
import WishlistBottomSheet from "./WishlistBottomSheet";
import { motion, AnimatePresence } from "framer-motion";

interface Props {
  recipientEmail: string;
  recipientName: string;
}

const RecipientWishlistsInline = ({ recipientEmail }: Props) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [wishlists, setWishlists] = useState<Wishlist[]>([]);
  const [itemsByList, setItemsByList] = useState<Record<string, WishItem[]>>({});
  const [loading, setLoading] = useState(true);
  const [activeListId, setActiveListId] = useState<string | null>(null);
  const [openSheetList, setOpenSheetList] = useState<Wishlist | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const [, setCanScrollLeft] = useState(false);
  const [, setCanScrollRight] = useState(false);

  useEffect(() => {
    if (!recipientEmail) { setLoading(false); return; }
    let active = true;

    (async () => {
      const { data } = await getGiftDataByRecipientEmail(recipientEmail);
      if (!active) return;

      const allLists = data?.wishlists?.filter((w) => w.type === "wishlist" || w.type === "registry") || [];

      if (allLists.length > 0) {
        const results = await Promise.all(allLists.map((w) => getWishItems(w.id)));
        if (!active) return;
        const map: Record<string, WishItem[]> = {};
        allLists.forEach((w, i) => { map[w.id] = results[i].data || []; });
        // Only keep lists that have at least one item
        const nonEmpty = allLists.filter((w) => (map[w.id]?.length ?? 0) > 0);
        setItemsByList(map);
        setWishlists(nonEmpty);
        if (nonEmpty.length > 0) setActiveListId(nonEmpty[0].id);
      } else {
        setWishlists([]);
      }

      setLoading(false);
    })();

    return () => { active = false; };
  }, [recipientEmail]);

  const checkScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 2);
    setCanScrollRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 4);
  };

  useEffect(() => {
    checkScroll();
  }, [activeListId, itemsByList]);


  const handleClaim = async (itemId: string) => {
    if (!user) return;
    const displayName = user.user_metadata?.display_name || user.email || "Someone";
    const { error } = await updateWishItem(itemId, {
      is_claimed: true,
      claimed_by_name: displayName,
      claimed_at: new Date().toISOString(),
    });
    if (!error) {
      setItemsByList((prev) => {
        const next = { ...prev };
        for (const key of Object.keys(next)) {
          next[key] = next[key].map((i) =>
            i.id === itemId
              ? { ...i, is_claimed: true, claimed_by_name: displayName, claimed_at: new Date().toISOString() }
              : i
          );
        }
        return next;
      });
      toast({ title: "Claimed!", description: "This wish has been marked as yours." });
    }
  };

  if (loading) {
    return (
      <div className="w-full rounded-2xl bg-card border border-border p-6 flex items-center justify-center gap-2">
        <Loader2 size={16} className="animate-spin text-primary" />
        <span className="text-sm text-muted-foreground">Loading wishlists…</span>
      </div>
    );
  }

  if (wishlists.length === 0) return null;

  const activeItems = activeListId ? (itemsByList[activeListId] || []) : [];
  const activeList = wishlists.find((w) => w.id === activeListId);

  return (
    <div className="w-full space-y-4">
      {/* Section header removed — show only images */}


      {/* List tabs — refined, outlined active state */}
      {wishlists.length > 1 && (
        <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1 scrollbar-hide">
          {wishlists.map((wl) => {
            const items = itemsByList[wl.id] || [];
            const unclaimedCount = items.filter((i) => !i.is_claimed).length;
            const isActive = activeListId === wl.id;

            return (
              <button
                key={wl.id}
                onClick={() => setActiveListId(wl.id)}
                className={`shrink-0 flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-[11px] font-medium tracking-wide transition-all border ${
                  isActive
                    ? "bg-foreground text-background border-foreground"
                    : "bg-background text-foreground/70 border-border hover:border-foreground/40 hover:text-foreground"
                }`}
              >
                {wl.type === "registry" ? <ShoppingBag size={11} /> : <Gift size={11} />}
                <span>{wl.title}</span>
                {unclaimedCount > 0 && (
                  <span className={`inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[9px] font-bold ${
                    isActive ? "bg-background/20 text-background" : "bg-muted text-foreground/70"
                  }`}>
                    {unclaimedCount}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      )}

      {/* Carousel */}
      <AnimatePresence mode="wait">
        {activeListId && activeItems.length > 0 && (
          <motion.div
            key={activeListId}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.2 }}
            className="space-y-3"
          >
            <div className="grid grid-cols-3 gap-2.5">
              {activeItems.slice(0, 6).map((item) => (
                <CarouselProductCard
                  key={item.id}
                  item={item}
                  isAuthenticated={!!user}
                  onClaim={handleClaim}
                />
              ))}
            </div>

            {/* View full list — opens bottom sheet */}
            {activeList && (
              <button
                type="button"
                onClick={() => setOpenSheetList(activeList)}
                className="group/link mx-auto flex items-center gap-2 text-[11px] font-medium tracking-[0.12em] uppercase text-foreground/70 hover:text-foreground transition-colors py-2 w-fit"
              >
                <span className="h-px w-6 bg-foreground/30 group-hover/link:bg-foreground/60 transition-colors" />
                View full list
                <ChevronRight size={12} className="transition-transform group-hover/link:translate-x-0.5" />
              </button>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {openSheetList && (
        <WishlistBottomSheet
          wishlist={openSheetList}
          onClose={() => setOpenSheetList(null)}
        />
      )}
    </div>
  );
};

/** Premium compact product card for horizontal carousel */
function CarouselProductCard({
  item,
}: {
  item: WishItem;
  isAuthenticated: boolean;
  onClaim: (id: string) => void;
}) {
  const isClaimed = !!item.is_claimed;

  return (
    <div
      className={`w-full rounded-[20px] overflow-hidden group bg-card transition-all ${
        isClaimed ? "opacity-55" : ""
      }`}
    >
      {/* Image — object-contain so nothing is cropped */}
      <div className="relative w-full aspect-square bg-muted/30 overflow-hidden flex items-center justify-center p-3">
        {item.image_url ? (
          <BlurImage
            src={item.image_url}
            alt={item.title}
            className={`max-w-full max-h-full object-contain transition-transform duration-300 group-hover:scale-105 ${isClaimed ? "grayscale" : ""}`}
          />
        ) : (
          <Gift size={28} className="text-muted-foreground/20" />
        )}

        {/* Top pick badge */}
        {item.is_most_desired && !isClaimed && (
          <div className="absolute top-2 left-2 bg-foreground text-background rounded-full px-2 py-0.5 flex items-center gap-1 shadow-sm">
            <Star size={8} fill="currentColor" />
            <span className="text-[9px] font-bold uppercase tracking-wider">Top</span>
          </div>
        )}

        {/* External link icon */}
        {item.link_url && !isClaimed && (
          <a
            href={item.link_url}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            aria-label="Open product link"
            className="absolute top-2 right-2 w-6 h-6 rounded-full bg-background/90 backdrop-blur-sm border border-border/60 flex items-center justify-center text-foreground/70 hover:text-foreground hover:bg-background transition-all"
          >
            <ExternalLink size={11} />
          </a>
        )}

        {/* Claimed overlay */}
        {isClaimed && (
          <div className="absolute inset-0 bg-background/50 backdrop-blur-[1px] flex items-center justify-center">
            <div className="bg-foreground text-background rounded-full px-3 py-1 flex items-center gap-1">
              <Check size={10} strokeWidth={3} />
              <span className="text-[9px] font-bold uppercase tracking-wider">Claimed</span>
            </div>
          </div>
        )}
      </div>

      {/* Info hidden — image-only mode */}

    </div>
  );
}

export default RecipientWishlistsInline;
